import moment from 'moment-timezone';
import isEmpty from 'lodash/isEmpty';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import { isMidnightish } from '~/core/util/time';
import DateTime, { YEAR_MONTH_DAY_DASHED } from '~/components/DateTime';
import {
  getTodayHours,
  hasKnownHours,
  isAlwaysOpen,
  isClosingSoon,
  isOpenNow,
  isOpenSoon,
  isOutOfBusiness,
} from '~/core/util/location';
import {
  CLOSED_NOW_TEXT,
  CLOSING_SOON_TEXT,
  OPEN_ALWAYS_TEXT,
  OPEN_NOW_TEXT,
  OPENING_SOON_TEXT,
  PERMANENTLY_CLOSED_TEXT,
  UNKNOWN_HOURS_TEXT,
} from '~/constants/clinicDetailPage';

export enum ClinicStatusType {
  PermanentlyClosed,
  UnknownHours,
  ClosingSoon,
  AlwaysOpen,
  OpenNow,
  OpeningSoon,
  ClosedNow,
}

export type ClinicStatus = {
  type: ClinicStatusType;
  display: string;
  /** An extra display string used for some status types */
  additionalInfo?: string;
  /** The time component shown in additionalInfo as a standalone string, used when
   * the predetermined display strings in display & additionalInfo do not fit the use case */
  formattedTime?: string;
};

const formatTime = (time: any) => {
  const timeObject = moment(time);
  if (timeObject.minute() > 0) {
    return timeObject.format('h:mma');
  }

  return timeObject.format('ha');
};

const todayTime = (time: any) => {
  if (isEmpty(time)) return null;

  return isMidnightish(time) ? '00:00:00' : time;
};

export const useClinicStatus = (
  location: LocationInterface,
  hideTimezone?: boolean
): ClinicStatus => {
  const dateString = DateTime.format(moment.tz(), location.timeZone)(YEAR_MONTH_DAY_DASHED);
  const todayHours = getTodayHours(location);
  const todayOpeningTime = todayTime(todayHours.fromTime);
  const todayClosingTime = todayTime(todayHours.toTime);
  const hasUnknownHours = !hasKnownHours(location.hours);

  const getAdditionalInfo = () => {
    // if location is closed permanently or has unknown hours -
    // we don't show the additionalInfo

    if (hasUnknownHours || isOutOfBusiness(location) || isAlwaysOpen(location)) {
      return {};
    }

    const { timeZone } = location;
    const timeZoneAbbreviation = hideTimezone ? '' : ` (${moment().tz(timeZone).zoneName()})`;

    if (isOpenSoon(location)) {
      const formattedTime = `${formatTime(
        `${dateString} ${todayOpeningTime}`
      )}${timeZoneAbbreviation}`;

      return {
        additionalInfo: `Opens ${formattedTime}`,
        formattedTime,
      };
    }

    const formattedTime = `${formatTime(
      `${dateString} ${todayClosingTime}`
    )}${timeZoneAbbreviation}`;

    return {
      additionalInfo: `Closes ${formattedTime}`,
      formattedTime,
    };
  };

  if (isOutOfBusiness(location)) {
    return {
      type: ClinicStatusType.PermanentlyClosed,
      display: PERMANENTLY_CLOSED_TEXT,
    };
  }
  if (hasUnknownHours) {
    return {
      type: ClinicStatusType.UnknownHours,
      display: UNKNOWN_HOURS_TEXT,
    };
  }
  if (isClosingSoon(location)) {
    return {
      type: ClinicStatusType.ClosingSoon,
      display: CLOSING_SOON_TEXT,
      ...getAdditionalInfo(),
    };
  }
  if (isAlwaysOpen(location)) {
    return {
      type: ClinicStatusType.AlwaysOpen,
      display: OPEN_ALWAYS_TEXT,
    };
  }
  if (isOpenNow(location)) {
    return {
      type: ClinicStatusType.OpenNow,
      display: OPEN_NOW_TEXT,
      ...getAdditionalInfo(),
    };
  }
  if (isOpenSoon(location)) {
    return {
      type: ClinicStatusType.OpeningSoon,
      display: OPENING_SOON_TEXT,
      ...getAdditionalInfo(),
    };
  }

  return {
    type: ClinicStatusType.ClosedNow,
    display: CLOSED_NOW_TEXT,
  };
};
